import { EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, Menu } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { useMemo } from 'react';
import { useStore } from 'zustand';

import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { inventoryStore } from '../../InventoryStore';

import styles from './DuplicationMenu.module.scss';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export enum DuplicationMode {
  END_OF_WEEK = 'END_OF_WEEK',
  END_OF_NEXT_WEEK = 'END_OF_NEXT_WEEK',
  END_OF_TABLE = 'END_OF_TABLE',
  CUSTOM = 'CUSTOM',
}

export type DuplicationModeData =
  | { mode: DuplicationMode.END_OF_WEEK }
  | { mode: DuplicationMode.END_OF_NEXT_WEEK }
  | { mode: DuplicationMode.END_OF_TABLE }
  | { mode: DuplicationMode.CUSTOM; customDay: Dayjs };

type DuplicationRange = {
  start: Dayjs;
  end: Dayjs;
};

type DuplicationMenuProps = {
  date: Dayjs;
  currentRange: DuplicationRange;
  onDuplicate: (modeData: DuplicationModeData) => void;
  onHover?: (day: Dayjs, hoverMode: DuplicationModeData) => void;
  onHoverEnd?: () => void;
  disabled?: boolean;
};

type DuplicationMenuContentProps = Omit<DuplicationMenuProps, 'onDuplicate'> & {
  onSelect: (modeData: DuplicationModeData) => void;
};

const DuplicationMenuContent: React.FC<DuplicationMenuContentProps> = ({
  date,
  currentRange,
  onSelect,
  onHover,
  onHoverEnd,
}) => {
  const disableDuplicateToEndOfWeek = useMemo(() => {
    const isEndOfWeek = date.day() === 0;
    const endOfWeekDay = date.endOf('isoWeek');

    const isCurrentWeekIncomplete =
      endOfWeekDay.isAfter(currentRange.end, 'day') &&
      !endOfWeekDay.isSame(currentRange.end, 'day');

    return isEndOfWeek || isCurrentWeekIncomplete;
  }, [date, currentRange]);

  const disableDuplicateToEndOfNextWeek = useMemo(() => {
    const isInLastWeek = date.isoWeek() === currentRange.end.isoWeek();
    const endOfNextWeekDay = date.add(1, 'week').endOf('isoWeek');

    const isNextWeekIncomplete =
      endOfNextWeekDay.isAfter(currentRange.end, 'day') &&
      !endOfNextWeekDay.isSame(currentRange.end, 'day');

    return isInLastWeek || isNextWeekIncomplete;
  }, [date, currentRange]);

  const disableDuplicateToEndOfPeriod = useMemo(
    () => date.isSame(currentRange.end, 'day'),
    [date, currentRange]
  );

  const remainingRange = useMemo(() => {
    if (disableDuplicateToEndOfPeriod) {
      return undefined;
    }

    return {
      start: date,
      end: currentRange.end.subtract(1, 'day'),
    };
  }, [date, currentRange.end, disableDuplicateToEndOfPeriod]);

  const remainingDays = useMemo(() => {
    if (!remainingRange) {
      return [];
    }

    const days: Dayjs[] = [];
    let currentDay = remainingRange.start.add(1, 'day');

    while (currentDay.isSameOrBefore(remainingRange.end, 'day')) {
      days.push(currentDay);
      currentDay = currentDay.add(1, 'day');
    }

    return days;
  }, [remainingRange]);

  const getNumberOfDaysLabel = (targetDay: Dayjs) => {
    const numberOfDays = targetDay.diff(date, 'day');

    return pluralize(numberOfDays, 'day', 'days');
  };

  const handleMouseEnter = (modeData: DuplicationModeData) => {
    onHover?.(date, modeData);
  };

  const handleMouseLeave = () => {
    onHoverEnd?.();
  };

  return (
    <Menu
      items={[
        {
          key: 'duplicate-until-the-end-of-week',
          disabled: disableDuplicateToEndOfWeek,
          onMouseEnter: () =>
            handleMouseEnter({ mode: DuplicationMode.END_OF_WEEK }),
          onMouseLeave: handleMouseLeave,
          onClick: () => onSelect({ mode: DuplicationMode.END_OF_WEEK }),
          label: 'Duplicate until the end of next week',
        },
        {
          key: 'duplicate-until-the-end-of-next-week',
          disabled: disableDuplicateToEndOfNextWeek,
          onMouseEnter: () =>
            handleMouseEnter({ mode: DuplicationMode.END_OF_NEXT_WEEK }),
          onMouseLeave: handleMouseLeave,
          onClick: () => onSelect({ mode: DuplicationMode.END_OF_NEXT_WEEK }),
          label: 'Duplicate until the end of next week',
        },
        {
          key: 'duplicate-until-the-end-of-period',
          disabled: disableDuplicateToEndOfPeriod,
          onMouseEnter: () =>
            handleMouseEnter({ mode: DuplicationMode.END_OF_TABLE }),
          onMouseLeave: handleMouseLeave,
          onClick: () => onSelect({ mode: DuplicationMode.END_OF_TABLE }),
          label: `Duplicate until the end of period (${currentRange.end.format(
            'ddd DD/MM'
          )})`,
        },
        {
          key: 'duplicate-until-the-day-of-my-choice',
          disabled: disableDuplicateToEndOfPeriod,
          label: 'Duplicate until the day of my choice',
          children: remainingDays.map((day) => ({
            key: `custom-${day.format('YYYY-MM-DD')}`,
            onMouseEnter: () =>
              handleMouseEnter({
                mode: DuplicationMode.CUSTOM,
                customDay: day,
              }),
            onMouseLeave: handleMouseLeave,
            onClick: () =>
              onSelect({
                mode: DuplicationMode.CUSTOM,
                customDay: day,
              }),
            label: `${getNumberOfDaysLabel(day)} (${day.format('ddd DD/MM')})`,
          })),
        },
      ]}
    />
  );
};

export const DuplicationMenu: React.FC<DuplicationMenuProps> = ({
  date,
  currentRange,
  onDuplicate,
  onHover,
  onHoverEnd,
  disabled = false,
}) => {
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const tab = useStore(inventoryStore, (state) => state.tab);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      amplitudeTrack('Click Line Duplication', {
        from: date.format('YYYY-MM-DD'),
        tab: tab.name,
      });
    }
  };

  if (!isEditMode) {
    return null;
  }

  return (
    <Dropdown
      dropdownRender={() => (
        <DuplicationMenuContent
          date={date}
          currentRange={currentRange}
          onSelect={onDuplicate}
          onHover={onHover}
          onHoverEnd={onHoverEnd}
        />
      )}
      trigger={['click']}
      onOpenChange={handleOpenChange}
      disabled={disabled}
    >
      <EllipsisOutlined rotate={90} className={styles.ellipsisIcon} />
    </Dropdown>
  );
};
